(() => {
  if (customElements.get('ck-tiered-accessories')) return;
  customElements.define('ck-tiered-accessories', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.config = JSON.parse(this.querySelector('[data-variants]').textContent);
      this.variant = this.config.variants.find(v => v.id === this.config.selected);
      this.cart = {items: [], item_count: 0};
      this.busy = false;
      this.initialized = false;
      this.abort = new AbortController();
      this.refresh = this.refresh.bind(this);
      this.querySelectorAll('[data-variant]').forEach(button => button.addEventListener('click', () => {
        if (this.busy) return;
        this.variant = this.config.variants.find(v => v.id === Number(button.dataset.variant));
        this.render();
        const img = this.querySelector('img');
        if (img && this.variant.image) { img.src = this.variant.image; img.removeAttribute('srcset'); img.alt = this.variant.title; }
      }));
      this.querySelector('[data-plus]').addEventListener('click', () => this.change(1));
      this.querySelector('[data-minus]').addEventListener('click', () => this.change(-1));
      ['theme:cart:change','theme:cart:update'].forEach(name => document.addEventListener(name, this.refresh, {signal:this.abort.signal}));
      window.addEventListener('pageshow', this.refresh, {signal:this.abort.signal});
      window.addEventListener('focus', this.refresh, {signal:this.abort.signal});
      this.render();
      this.refresh();
    }
    disconnectedCallback() { this.abort?.abort(); this.ready = false; }
    route(path) { return (window.Shopify?.routes?.root || '/') + path; }
    async request(path, body) {
      const response = await fetch(this.route(path), body ? {method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(body)} : {cache:'no-store'});
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || data.message || (this.config.locale === 'ja' ? 'カートを更新できませんでした。もう一度お試しください。' : this.config.locale === 'de' ? 'Der Warenkorb konnte nicht aktualisiert werden. Bitte versuche es erneut.' : 'Unable to update cart. Please try again.'));
      return data;
    }
    async refresh() {
      const revision = this.revision = (this.revision || 0) + 1;
      try {
        const cart = await this.request('cart.js');
        if (revision !== this.revision || !this.isConnected) return;
        this.cart = cart; this.initialized = true; this.render();
      } catch (error) { if (!this.initialized) this.error(error.message); }
    }
    error(message) { const el = this.querySelector('[data-error]'); el.textContent = message; el.hidden = !message; }
    render() {
      const accessoryLink = this.querySelector('[data-accessory-link]');
      if (accessoryLink && this.variant) {
        const destination = new URL(accessoryLink.href);
        destination.searchParams.set('variant', this.variant.id);
        accessoryLink.href = destination.href;
      }
      // Automatic zero-price gifts are not qualifying purchased items in Shopify's discount minimum.
      const count = this.cart.items.reduce((sum, item) => {
        const gift = item.properties?.['_CASEKOO Sticker Gift'] === 'Yes';
        return sum + (!gift && Number(item.original_price ?? item.price) > 0 ? item.quantity : 0);
      }, 0);
      this.purchasedCount = count;
      const tier = Math.min(count,3);
      const track = this.querySelector('[role=progressbar]');
      track.setAttribute('aria-valuenow',tier);
      track.setAttribute('aria-valuetext',this.config.locale === 'ja' ? `${count}点、${[0,10,20,30][tier]}%OFF` : this.config.locale === 'de' ? `${count} Artikel, ${[0,10,20,30][tier]} % Rabatt` : `${count} items, ${[0,10,20,30][tier]}% OFF`);
      track.querySelector('i').style.width = `${tier / 3 * 100}%`;
      this.querySelectorAll('[data-tier]').forEach(el => el.classList.toggle('is-active', Number(el.dataset.tier) <= tier));
      const quantity = this.cart.items.filter(item => item.variant_id === this.variant.id).reduce((sum,item) => sum + item.quantity,0);
      this.querySelector('[data-quantity]').textContent = quantity;
      this.querySelector('[data-minus]').disabled = this.busy || !this.initialized || quantity === 0;
      this.querySelector('[data-plus]').disabled = this.busy || !this.initialized || !this.variant.available;
      this.querySelectorAll('[data-variant]').forEach(el => {el.setAttribute('aria-pressed', String(Number(el.dataset.variant) === this.variant.id)); el.disabled = this.busy || !this.config.variants.find(v => v.id === Number(el.dataset.variant)).available;});
      this.querySelector('[data-price]').textContent = new Intl.NumberFormat(this.config.locale,{style:'currency',currency:this.config.currency}).format(this.variant.price / 100);
      this.setAttribute('aria-busy',String(this.busy));
    }
    async change(delta) {
      if (this.busy || !this.initialized) return;
      this.busy = true; this.error(''); this.render();
      try {
        // Re-read before reducing a line; never overwrite another product's quantity.
        this.revision = (this.revision || 0) + 1;
        this.cart = await this.request('cart.js');
        const line = this.cart.items.find(item => item.variant_id === this.variant.id);
        if (delta < 0 && !line) return;
        const sections = 'cart-items,cart-footer,cart-item-count,cart-live-region';
        const body = {sections, sections_url:window.location.pathname};
        const response = delta > 0
          ? await this.request('cart/add.js',{...body,items:[{id:this.variant.id,quantity:1}]})
          : await this.request('cart/change.js',{...body,id:line.key,quantity:line.quantity - 1});
        this.cart = await this.request('cart.js');
        window.theme.cartItemCount = this.cart.item_count;
        this.render();
        if (response.sections && Object.values(response.sections).every(Boolean)) {
          document.body.dispatchEvent(new CustomEvent('shapes:cart:cartqtychange',{bubbles:true,detail:{response:{...this.cart,sections:response.sections},variantId:this.variant.id,key:line?.key,quantity:this.cart.items.filter(i=>i.variant_id===this.variant.id).reduce((s,i)=>s+i.quantity,0)}}));
        }
        document.dispatchEvent(new CustomEvent('theme:cart:change',{detail:{cartItemCount:this.cart.item_count}}));
      } catch (error) { this.error(error.message); await this.refresh(); }
      finally { this.busy = false; this.render(); }
    }
  });
})();
